// ./src/contexts/DashboardContext.tsx
//
// Context wrapper around the useDashboard hook.
//
// Purpose:
// - Makes dashboard data (cars + stats + active car) available to all
//   components without prop-drilling.
// - Replaces both CarsProvider and CarStatsProvider with a single unified
//   context that loads everything in one API call.
// - Wraps the top-level private pages so all authenticated routes share
//   the same dashboard state.
//
// Usage:
//
//   // In the app layout / route wrapper:
//   <DashboardProvider autoLoad>
//     <PrivateRoutes />
//   </DashboardProvider>
//
//   // In any child component:
//   const { items, cars, activeCar, refresh, ... } = useDashboardContext();
//
//   // In a component that may or may not be inside a provider:
//   const context = useOptionalDashboardContext();
//   const cars = context?.cars ?? [];

import React, { createContext, useContext } from "react";

import {
  useDashboard,
  UseDashboardOptions,
  UseDashboardReturn,
} from "@/hooks/useDashboard";

// ============================================
// Context
// ============================================

const DashboardContext = createContext<UseDashboardReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface DashboardProviderProps extends UseDashboardOptions {
  children: React.ReactNode;
}

/**
 * Wraps useDashboard into a context provider.
 *
 * Replaces the previous CarsProvider + CarStatsProvider combination.
 * Loads cars and their stats in a single API call via dashboardGet.
 *
 * After any expense/revenue add/update/remove, call `refresh()` to reload.
 *
 * @example
 * <DashboardProvider autoLoad initialParams={{ includeCars: true, avgMonths: 3 }}>
 *   <AppShell>
 *     <PrivateRoutes />
 *   </AppShell>
 * </DashboardProvider>
 */
export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  ...options
}) => {
  const dashboard = useDashboard(options);

  return (
    <DashboardContext.Provider value={dashboard}>
      {children}
    </DashboardContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the DashboardContext. Throws if used outside a DashboardProvider.
 *
 * Use this in components that are always rendered inside the provider
 * (e.g. DashboardPage, VehicleCards, StatsOverview).
 */
export const useDashboardContext = (): UseDashboardReturn => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error(
      "useDashboardContext must be used within a <DashboardProvider>. " +
      "Wrap your component tree with <DashboardProvider> or use useOptionalDashboardContext() instead."
    );
  }

  return context;
};

/**
 * Access the DashboardContext if available, or return null.
 *
 * Use this in components that may or may not be inside the provider
 * (e.g. shared components used on both authenticated and public pages).
 *
 * @example
 * const context = useOptionalDashboardContext();
 * const activeCar = context?.activeCar;
 * const carStats = context?.getItemByCarId(carId);
 */
export const useOptionalDashboardContext = (): UseDashboardReturn | null => {
  return useContext(DashboardContext);
};